/* eslint-disable max-len */
import React, { useMemo, useState } from 'react';
import { CButton, CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell } from '@coreui/react';
import { useTranslation } from '../../hooks/useTranslation';
import './ExportData.scss';

function ExportData(props) {
  const {
    columns = [],
    rows = [],
    allRows = [], // Tüm filtrelenmiş veri (sayfalanmamış)
    buildCellValue,
    filenamePrefix = 'export',
    triggerClassName = 'export-btn-enhanced',
    triggerText,
    triggerIcon = '📊',
    triggerDisabled = false,
    modalSize = 'xl',
  } = props;

  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportScope, setExportScope] = useState('current'); // 'current' or 'all'
  const [previewModal, setPreviewModal] = useState(false);

  // Ensure we don't export control columns even if they are provided
  const exportableColumns = useMemo(() => {
    const excluded = new Set(['actions', 'expand', 'index']);
    return (columns || []).filter(c => !excluded.has(c.id));
  }, [columns]);

  // Heuristics for treating cells as text to prevent Excel scientific notation
  const isIdLike = (colId) => /(^|[^a-z])(id|guid)([^a-z]|$)/i.test(colId || '');
  const isLargeNumericString = (val) => {
    if (val == null) return false;
    const str = String(val);
    return /^\d{15,}$/.test(str); // 15+ digits risk precision/scientific formatting in Excel
  };

  const effectiveBuildCellValue = (colId, item) => {
    if (typeof buildCellValue === 'function') return buildCellValue(colId, item);
    return item[colId] ?? '';
  };

  const buildExportRows = () => {
    const dataToExport = exportScope === 'all' ? (allRows || []) : (rows || []);
    return dataToExport.map(item => exportableColumns.map(c => effectiveBuildCellValue(c.id, item)));
  };

  // Build rows with column metadata (for Excel styling needs)
  const buildExportRowsWithMeta = () => {
    const dataToExport = exportScope === 'all' ? (allRows || []) : (rows || []);
    return dataToExport.map(item => exportableColumns.map(c => ({ colId: c.id, value: effectiveBuildCellValue(c.id, item) })));
  };

  const getDataToExport = () => {
    return exportScope === 'all' ? (allRows || []) : (rows || []);
  };

  const csvEscape = (value) => {
    const str = value == null ? '' : String(value);
    if (/[",\n]/.test(str)) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  };

  const escapeHtml = (unsafe) => {
    return String(unsafe)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const exportToCSV = () => {
    const headers = exportableColumns.map(c => c.label);
    const dataToExport = exportScope === 'all' ? (allRows || []) : (rows || []);
    const rowsData = dataToExport.map(item => exportableColumns.map(c => {
      const raw = effectiveBuildCellValue(c.id, item);
      const str = raw == null ? '' : String(raw);
      if (isIdLike(c.id) || isLargeNumericString(str)) {
        // Leading apostrophe forces text in Excel CSV import
        return "'" + str;
      }
      return str;
    }));
    const csv = [headers.join(','), ...rowsData.map(r => r.map(csvEscape).join(','))].join('\n');
    const scopeSuffix = exportScope === 'all' ? '_all' : '_current';
    const filename = `${filenamePrefix}${scopeSuffix}_${new Date().toISOString().slice(0, 10)}.csv`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportToXLS = () => {
    const weightLikeIds = new Set(['TotalWarpWeight', 'TotalWeftWeight']);
    const dataToExport = exportScope === 'all' ? (allRows || []) : (rows || []);

    // Compute reasonable column widths based on labels and sample values
    const approxCharPx = 7;
    const basePx = 24;
    const minPxDefault = 140;
    const minPxForId = 220;
    const maxPx = 600;
    const sampleLimit = 200;

    const widths = exportableColumns.map((c) => {
      const labelLen = String(c.label || '').length;
      let maxLen = labelLen;
      for (let i = 0; i < Math.min(sampleLimit, dataToExport.length); i++) {
        const raw = effectiveBuildCellValue(c.id, dataToExport[i]);
        const str = raw == null ? '' : String(raw);
        if (str.length > maxLen) maxLen = str.length;
      }
      const minPx = isIdLike(c.id) ? minPxForId : minPxDefault;
      const w = Math.max(minPx, Math.min(maxPx, basePx + approxCharPx * maxLen));
      return Math.round(w);
    });

    const colGroup = `<colgroup>${widths.map(w => `<col style="width:${w}px" width="${w}" />`).join('')}</colgroup>`;

    const headers = exportableColumns
      .map((c, i) => `<th style="width:${widths[i]}px" width="${widths[i]}">${escapeHtml(c.label)}</th>`)
      .join('');

    const rowsHtml = dataToExport.map(item => {
      const tds = exportableColumns.map((c, i) => {
        const raw = effectiveBuildCellValue(c.id, item);
        const str = raw == null ? '' : String(raw);
        const isText = weightLikeIds.has(c.id) || /weight/i.test(c.id) || isIdLike(c.id) || isLargeNumericString(str);
        const numberFormat = isText ? "mso-number-format:'\\@';" : '';
        return `<td style="width:${widths[i]}px;${numberFormat}" width="${widths[i]}">${escapeHtml(str)}</td>`;
      }).join('');
      return `<tr>${tds}</tr>`;
    }).join('');

    const table = `<!DOCTYPE html><html><head><meta charset="utf-8" /></head><body><table border="1">${colGroup}<thead><tr>${headers}</tr></thead><tbody>${rowsHtml}</tbody></table></body></html>`;
    const scopeSuffix = exportScope === 'all' ? '_all' : '_current';
    const filename = `${filenamePrefix}${scopeSuffix}_${new Date().toISOString().slice(0, 10)}.xls`;
    const blob = new Blob([table], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const dataToExport = getDataToExport();
    const printWindow = window.open('', '_blank');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${filenamePrefix} - ${new Date().toLocaleDateString()}</title>
        <style>
          * {
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            margin: 20px;
            color: #333;
          }
          .print-header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
          }
          .print-header h1 {
            margin: 0;
            color: #333;
            font-size: 24px;
          }
          .print-header .meta-info {
            margin-top: 10px;
            color: #666;
            font-size: 14px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
            break-inside: avoid;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            font-size: 11px;
            word-wrap: break-word;
          }
          th {
            background-color: #f8f9fa;
            font-weight: bold;
            color: #333;
            text-transform: uppercase;
            font-size: 10px;
            letter-spacing: 0.5px;
          }
          tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          tr:hover {
            background-color: #f5f5f5;
          }
          .print-footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            color: #666;
            border-top: 1px solid #ddd;
            padding-top: 15px;
          }
          @media print {
            body {
              margin: 0;
              padding: 15px;
            }
            .print-header {
              margin-bottom: 20px;
            }
            table {
              page-break-inside: auto;
            }
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            th {
              page-break-after: avoid;
            }
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <h1>${filenamePrefix.replace(/_/g, ' ').toUpperCase()}</h1>
          <div class="meta-info">
            <div><strong>${t('printDate') || 'Yazdırma Tarihi'}:</strong> ${new Date().toLocaleString()}</div>
            <div><strong>${t('totalRecords') || 'Toplam Kayıt'}:</strong> ${dataToExport.length}</div>
            <div><strong>${t('scope') || 'Kapsam'}:</strong> ${exportScope === 'all' ? (t('allResults') || 'Tüm Sonuçlar') : (t('currentPage') || 'Mevcut Sayfa')}</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              ${exportableColumns.map(col => `<th>${escapeHtml(col.label)}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${dataToExport.map(item => `
              <tr>
                ${exportableColumns.map(col => {
                  const value = effectiveBuildCellValue(col.id, item);
                  return `<td>${escapeHtml(value || '')}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="print-footer">
          <div>${t('generatedBy') || 'Tarafından oluşturuldu'}: ${window.location.hostname}</div>
          <div>${t('pageOf') || 'Sayfa'} <span class="page-number"></span></div>
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            }, 500);
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const handlePreview = () => {
    setPreviewModal(true);
  };

  const handleConfirmExport = () => {
    const dataToExport = exportScope === 'all' ? (allRows || []) : (rows || []);
    if (!dataToExport || dataToExport.length === 0) {
      setModalOpen(false);
      return;
    }
    if (exportFormat === 'xls') {
      exportToXLS();
    } else {
      exportToCSV();
    }
    setModalOpen(false);
  };

  const getCurrentPageRecordCount = () => rows?.length || 0;
  const getAllRecordCount = () => allRows?.length || 0;

  const previewData = useMemo(() => {
    const dataToShow = getDataToExport();
    return dataToShow.slice(0, 10); // İlk 10 kayıt önizleme
  }, [exportScope, rows, allRows]);

  return (
    <>
      <CButton
        onClick={() => setModalOpen(true)}
        className={triggerClassName}
        disabled={triggerDisabled}
        type="button"
      >
        <span className="export-icon">{triggerIcon}</span>
        <span className="export-text">{triggerText || t('export') || 'Dışa Aktar'}</span>
      </CButton>

      <CModal
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        alignment="center"
        size={modalSize}
        backdrop="static"
        className="export-modal-enhanced"
      >
        <CModalHeader className="export-modal-header">
          <CModalTitle className="export-modal-title">
            {t('exportData') || 'Veri Dışa Aktarımı'}
          </CModalTitle>
        </CModalHeader>

        <CModalBody className="export-modal-body">
          <p className="export-description">
            {t('selectExportOptions') || 'Dışa aktarma seçeneklerini belirleyin:'}
          </p>

          {/* Export Scope Selection */}
          <div className="export-scope-section">
            <h6 className="export-section-title">
              {t('exportScope') || 'Aktarım Kapsamı'}
            </h6>
            <div className="export-scope-toggle">
              <div className="scope-option-wrapper">
                <label className="scope-toggle-option">
                  <input
                    type="radio"
                    name="exportScope"
                    className="scope-radio-input"
                    checked={exportScope === 'current'}
                    onChange={() => setExportScope('current')}
                  />
                  <span className="scope-radio-custom"></span>
                  <div className="scope-content">
                    <div className="scope-title">
                      {t('exportCurrentPage') || 'Mevcut Sayfayı Aktar'}
                    </div>
                    <div className="scope-description">
                      {getCurrentPageRecordCount()} {t('recordsOnThisPage') || 'kayıt (bu sayfa)'}
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="export-actions-section">
            <h6 className="export-section-title">
              {t('actions') || 'İşlemler'}
            </h6>
            <div className="export-action-buttons">
              <CButton
                color="outline-primary"
                onClick={handlePreview}
                className="export-action-btn preview-btn"
                disabled={getDataToExport().length === 0}
              >
                <span className="action-icon">👁️</span>
                <span className="action-text">{t('preview') || 'Ön İzleme'}</span>
              </CButton>

              <CButton
                color="outline-info"
                onClick={handlePrint}
                className="export-action-btn print-btn"
                disabled={getDataToExport().length === 0}
              >
                <span className="action-icon">🖨️</span>
                <span className="action-text">{t('print') || 'Yazdır'}</span>
              </CButton>
            </div>
          </div>

          {/* Format Selection */}
          <div className="export-format-section">
            <h6 className="export-section-title">
              {t('exportFormat') || 'Dosya Formatı'}
            </h6>
            <div className="export-format-options">
              <div className="export-format-option">
                <label className="enhanced-radio-check">
                  <input
                    type="radio"
                    name="exportFormat"
                    className="form-check-input"
                    checked={exportFormat === 'csv'}
                    onChange={() => setExportFormat('csv')}
                  />
                  <span className="form-check-label">CSV - Comma Separated Values</span>
                </label>
              </div>

              <div className="export-format-option">
                <label className="enhanced-radio-check">
                  <input
                    type="radio"
                    name="exportFormat"
                    className="form-check-input"
                    checked={exportFormat === 'xls'}
                    onChange={() => setExportFormat('xls')}
                  />
                  <span className="form-check-label">Excel (.xls) - Microsoft Excel Format</span>
                </label>
              </div>
            </div>
          </div>

          <div className="export-info-section">
            <div className="export-info-content">
              <small>
                {(t('exportDetails') || 'Dışa aktarım detayları:')}<br />
                • <strong>
                  {exportScope === 'all' ? getAllRecordCount() : getCurrentPageRecordCount()}
                </strong> {(t('recordsWillBeExported') || 'kayıt dışa aktarılacak')}<br />
                • {(t('filtersApplied') || 'Uygulanan filtreler dahil edilecek')}<br />
                • {(t('actionColumnsExcluded') || 'Eylem sütunları hariç tutulacak')}
              </small>
            </div>
          </div>
        </CModalBody>

        <CModalFooter className="export-modal-footer">
          <CButton
            onClick={() => setModalOpen(false)}
            className="export-cancel-btn"
            type="button"
          >
            {t('cancel') || 'İptal'}
          </CButton>

          <CButton
            onClick={handleConfirmExport}
            className="export-confirm-btn"
            disabled={
              (exportScope === 'all' ? getAllRecordCount() : getCurrentPageRecordCount()) === 0
            }
            type="button"
          >
            <span className="export-confirm-icon download-icon"></span>
            <span className="export-confirm-text">
              {t('downloadFile') || 'Dosyayı İndir'}
            </span>
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Preview Modal */}
      <CModal
        visible={previewModal}
        onClose={() => setPreviewModal(false)}
        alignment="center"
        size="xl"
        backdrop="static"
        className="export-preview-modal"
      >
        <CModalHeader className="preview-modal-header">
          <CModalTitle className="preview-modal-title">
            {t('dataPreview') || 'Veri Ön İzleme'}
          </CModalTitle>
        </CModalHeader>

        <CModalBody className="preview-modal-body">
          <div className="preview-info">
            <div className="preview-meta">
              <span><strong>{t('scope') || 'Kapsam'}:</strong> {exportScope === 'all' ? (t('allResults') || 'Tüm Sonuçlar') : (t('currentPage') || 'Mevcut Sayfa')}</span>
              <span><strong>{t('totalRecords') || 'Toplam Kayıt'}:</strong> {getDataToExport().length}</span>
              <span><strong>{t('showing') || 'Gösterilen'}:</strong> {Math.min(10, getDataToExport().length)} {t('records') || 'kayıt'}</span>
            </div>
          </div>

          <div className="preview-table-container">
            <CTable hover responsive striped className="preview-table">
              <CTableHead>
                <CTableRow>
                  {exportableColumns.map((column) => (
                    <CTableHeaderCell key={column.id} className="preview-header">
                      {column.label}
                    </CTableHeaderCell>
                  ))}
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {previewData.length > 0 ? (
                  previewData.map((item, index) => (
                    <CTableRow key={index}>
                      {exportableColumns.map((column) => (
                        <CTableDataCell key={column.id} className="preview-cell">
                          {effectiveBuildCellValue(column.id, item) || '-'}
                        </CTableDataCell>
                      ))}
                    </CTableRow>
                  ))
                ) : (
                  <CTableRow>
                    <CTableDataCell colSpan={exportableColumns.length} className="text-center py-4">
                      <div className="text-muted">{t('noDataFound') || 'Veri bulunamadı'}</div>
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>
          </div>

          {getDataToExport().length > 10 && (
            <div className="preview-note">
              <small className="text-muted">
                {t('previewNote') || 'Not: Sadece ilk 10 kayıt gösteriliyor. Tüm veriler dışa aktarılacak.'}
              </small>
            </div>
          )}
        </CModalBody>

        <CModalFooter className="preview-modal-footer">
          <CButton
            color="outline-info"
            onClick={handlePrint}
            className="preview-print-btn"
            disabled={getDataToExport().length === 0}
          >
            <span className="action-icon">🖨️</span>
            <span className="action-text">{t('print') || 'Yazdır'}</span>
          </CButton>

          <CButton
            onClick={() => setPreviewModal(false)}
            className="preview-close-btn"
            type="button"
          >
            {t('close') || 'Kapat'}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
}

export default ExportData;